import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import translate from '../../../../../commonComponents/translate';
import {
  newFormatPrice,
  formatNumbersWithZero,
  formatVA,
} from '../../../../../services/utilityFunctions';
import InfoIcon from '../../../../../commonComponents/icons/InfoIcon';

function formatChange(val, label) {
  if (val === null || val === undefined) return null;
  const isPositive = val >= 0;
  const arrow = isPositive ? '▲' : '▼';
  const sign = isPositive ? '+' : '';
  const cls = isPositive
    ? 'pureliink-va-block__change--positive'
    : 'pureliink-va-block__change--negative';
  return (
    <span className={cls}>
      {arrow} {sign}
      {val}% / {label}
    </span>
  );
}

function renderChangeDeltas(monthVal, yearVal) {
  const parts = [
    formatChange(monthVal, 'M-1'),
    formatChange(yearVal, 'Y-1'),
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return parts.reduce(
    (acc, part, i) =>
      i === 0
        ? [part]
        : [
            ...acc,
            <span key={i} className="change-separator">
              {' '}
              •{' '}
            </span>,
            part,
          ],
    [],
  );
}

function OverviewStats({ t, data, dateStart }) {
  if (!data) return null;

  const {
    lastPersonalOrderDate,
    honorificTrophyName,
    honorificTrophyIcon,
    totalCommissionsPaid,
    currencyInfo,
    personnalVA,
    groupVA,
    forecastLevelSkill,
    levelLastMonthSkill,
    honorificLevelSkill,
    coins,
    coupons,
    totalAffiliates,
    totalActiveAffiliates,
    averageVA,
  } = data;

  const isCurrentMonth =
    dateStart && moment(dateStart).isSame(moment(), 'month');

  const subStats = [
    {
      label: isCurrentMonth
        ? t('pureliink.career.overview.forecastLevel')
        : t('pureliink.career.overview.level'),
      value: forecastLevelSkill?.name,
      icon: forecastLevelSkill?.icon,
    },
    {
      label: t('pureliink.career.overview.levelLastMonth'),
      value: levelLastMonthSkill?.name,
      icon: levelLastMonthSkill?.icon,
    },
    {
      label: t('pureliink.career.overview.honorificLevel'),
      value: honorificLevelSkill?.name,
      icon: honorificLevelSkill?.icon,
    },
    {
      label: t('pureliink.career.overview.coins'),
      value: coins != null ? formatNumbersWithZero(coins, false, false) : '-',
    },
    {
      label: t('pureliink.career.overview.coupons'),
      value:
        coupons != null ? formatNumbersWithZero(coupons, false, false) : '-',
    },
    {
      label: t('pureliink.career.overview.totalAffiliates'),
      value:
        totalAffiliates != null
          ? formatNumbersWithZero(totalAffiliates, false, false)
          : '-',
    },
    {
      label: t('pureliink.career.overview.totalActive'),
      value:
        totalActiveAffiliates != null
          ? formatNumbersWithZero(totalActiveAffiliates, false, false)
          : '-',
      green: true,
    },
    {
      label: t('pureliink.career.overview.avgVA'),
      value:
        averageVA != null ? formatNumbersWithZero(averageVA, false, true) : '-',
      green: true,
      hint: t('pureliink.career.overview.avgVAHint'),
    },
  ];

  return (
    <>
      {/* Top 3 summary cards */}
      <div className="pureliink-overview-cards">
        <div className="pureliink-overview-card">
          <div className="pureliink-overview-card__label">
            📅 {t('pureliink.career.overview.lastPersonalOrder')}
          </div>
          <div className="pureliink-overview-card__value">
            {lastPersonalOrderDate
              ? moment(lastPersonalOrderDate).format('DD/MM/YYYY')
              : '-'}
          </div>
        </div>

        <div className="pureliink-overview-card pureliink-overview-card--trophy">
          <div className="pureliink-overview-card__label">
            🏆 {t('pureliink.career.overview.honorificTrophy')}
          </div>
          <div className="pureliink-overview-card__value pureliink-overview-card__value--trophy">
            {honorificTrophyIcon?.thumb && (
              <img src={honorificTrophyIcon.thumb} alt={honorificTrophyName} />
            )}
            {honorificTrophyName || '-'}
          </div>
        </div>

        <div className="pureliink-overview-card pureliink-overview-card--commissions">
          <div className="pureliink-overview-card__label">
            💰{' '}
            {isCurrentMonth
              ? t('pureliink.career.overview.commissionsEstimated')
              : t('pureliink.career.overview.commissionsPaid')}{' '}
            <InfoIcon
              width={14}
              height={15}
              tooltip={
                isCurrentMonth
                  ? t('pureliink.career.overview.commissionsEstimatedTooltip')
                  : t('pureliink.career.overview.commissionsPaidTooltip')
              }
            />
          </div>
          <div className="pureliink-overview-card__value pureliink-overview-card__value--commissions">
            {totalCommissionsPaid != null
              ? newFormatPrice(totalCommissionsPaid, true, currencyInfo?.code)
              : '-'}
          </div>
        </div>
      </div>

      {/* Dark stats card */}
      <div className="pureliink-dark-card">
        <div className="pureliink-dark-card__va-row">
          <div className="pureliink-va-block">
            <div className="pureliink-va-block__label">
              {t('pureliink.career.overview.personalVA')}
            </div>
            <div className="pureliink-va-block__value">
              {personnalVA?.current != null
                ? formatVA(personnalVA.current)
                : '-'}
            </div>
            <div className="pureliink-va-block__change">
              {renderChangeDeltas(
                personnalVA?.changeVsLastMonth,
                personnalVA?.changeVsLastYear,
              )}
            </div>
          </div>
          <div className="pureliink-va-block">
            <div className="pureliink-va-block__label">
              {t('pureliink.career.overview.groupVA')}
            </div>
            <div className="pureliink-va-block__value">
              {groupVA?.current != null ? formatVA(groupVA.current) : '-'}
            </div>
            <div className="pureliink-va-block__change">
              {renderChangeDeltas(
                groupVA?.changeVsLastMonth,
                groupVA?.changeVsLastYear,
              )}
            </div>
          </div>
        </div>

        <div className="pureliink-dark-card__stats-grid">
          {subStats.map(({ label, value, icon, green, hint }) => (
            <div key={label} className="pureliink-dark-stat">
              <div className="pureliink-dark-stat__label">{label}</div>
              <div
                className={`pureliink-dark-stat__value${
                  green ? ' pureliink-dark-stat__value--green' : ''
                }`}
              >
                {icon && <img src={icon} alt="" />}
                {value ?? '-'}
              </div>
              {hint && <div className="pureliink-dark-stat__hint">{hint}</div>}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

OverviewStats.propTypes = {
  t: PropTypes.func.isRequired,
  dateStart: PropTypes.number,
  data: PropTypes.shape({
    lastPersonalOrderDate: PropTypes.string,
    honorificTrophyName: PropTypes.string,
    honorificTrophyIcon: PropTypes.shape({
      thumb: PropTypes.string,
      medium: PropTypes.string,
      url: PropTypes.string,
    }),
    totalCommissionsPaid: PropTypes.number,
    currencyInfo: PropTypes.shape({
      symbol: PropTypes.string,
      code: PropTypes.string,
    }),
    personnalVA: PropTypes.shape({
      current: PropTypes.number,
      changeVsLastMonth: PropTypes.number,
      changeVsLastYear: PropTypes.number,
    }),
    groupVA: PropTypes.shape({
      current: PropTypes.number,
      changeVsLastMonth: PropTypes.number,
      changeVsLastYear: PropTypes.number,
    }),
    forecastLevelSkill: PropTypes.shape({
      icon: PropTypes.string,
      name: PropTypes.string,
    }),
    levelLastMonthSkill: PropTypes.shape({
      icon: PropTypes.string,
      name: PropTypes.string,
    }),
    honorificLevelSkill: PropTypes.shape({
      icon: PropTypes.string,
      name: PropTypes.string,
    }),
    coins: PropTypes.number,
    coupons: PropTypes.number,
    totalAffiliates: PropTypes.number,
    totalActiveAffiliates: PropTypes.number,
    averageVA: PropTypes.number,
  }),
};

OverviewStats.defaultProps = {
  dateStart: null,
  data: null,
};

export default translate(OverviewStats);
